import { useDisclosure } from '@chakra-ui/react';
import React, { PropsWithChildren, useCallback, useContext, useState } from 'react';
import NoteTopic from 'types/entities/note-topic.entity';

interface ModalProps {
  isOpen: boolean;
	onOpen: () => void;
  onClose: () => void;
}

interface AgendaContextValue {
	modals: {
    noteTopic: ModalProps,
		connectTopicToLists: ModalProps,
  };
	onOpenConnectTopicToLists: (topicId: string) => void;
	onCloseConnectTopicToLists: () => void;
	onCreateTopic: () => void;
	onEditTopic: (topic: NoteTopic) => () => void;
	topic: NoteTopic | null;
	topicId?: string | null;
}

const AgendaContext = React.createContext<AgendaContextValue | undefined>(
	undefined,
);

export const useAgendaContext = () => {
	const value = useContext(AgendaContext);
	if (value === undefined) {
		throw Error('useAgendaContext can not be used outside a AgendaProvider');
	}
	return value;
};

export default function AgendaProvider({ children }: PropsWithChildren) {
	const [topicId, setTopicId] = useState<string | null>();
	const [topic, setTopic] = useState<NoteTopic | null>();

  const modalNoteTopic = useDisclosure();
  const modalConnectTopicToLists = useDisclosure();

	const onCreateTopic = useCallback(() => {
		setTopic(null);
		modalNoteTopic.onOpen();
	}, [modalNoteTopic]);

	const onEditTopic = useCallback((topic: NoteTopic) => () => {
		setTopic(topic);
		modalNoteTopic.onOpen();
	}, [modalNoteTopic]);

	const onOpenConnectTopicToLists = useCallback((topicId: string) => {
		setTopicId(topicId);
		modalConnectTopicToLists.onOpen();
	}, [modalConnectTopicToLists, setTopicId]);

	const onCloseConnectTopicToLists = useCallback(() => {
		modalConnectTopicToLists.onClose();
		setTimeout(() => setTopicId(null), 500);
	}, [modalConnectTopicToLists, setTopicId]);

	return (
		<AgendaContext.Provider
			value={{
				modals: {
          noteTopic: modalNoteTopic,
					connectTopicToLists: modalConnectTopicToLists
        },
				onOpenConnectTopicToLists,
				onCloseConnectTopicToLists,
				onCreateTopic,
				onEditTopic,
				topicId,
				topic,
			}}>
			{children}
		</AgendaContext.Provider>
	);
}
